import { useState, useEffect, useRef } from 'react'
import { compressImages } from './imageCompression'
import './ChatThread.css'

const IcoSend = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
)
const IcoMessages = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
)
const IcoImage = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
)
const IcoX = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)
const IcoChevronLeft = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
)
const IcoChevronRight = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
)
const IcoDots = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" />
  </svg>
)
const IcoTrash = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" /><path d="M14 11v6" />
  </svg>
)
const IcoDoubleCheck = () => (
  <svg width="15" height="10" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 5.5L4.5 9L11 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5.5 5.5L9 9L15.5 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const initialsOf = (name = '') =>
  name.split(' ').map(n => n[0]).filter(Boolean).join('').toUpperCase().slice(0, 2) || '?'

const fmtPrice = (n) => (n === 0 ? 'Gratuit' : `${Number(n).toLocaleString('fr-FR')} €`)

// Formate un horodatage "dernière activité" façon WhatsApp : "aujourd'hui à 14:32", "hier à 09:10", "le 03/07 à 18:02"
const fmtLastSeen = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  if (d.toDateString() === now.toDateString()) return `aujourd'hui à ${time}`
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (d.toDateString() === yesterday.toDateString()) return `hier à ${time}`
  return `le ${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} à ${time}`
}

const MAX_IMAGES    = 6
const HARD_MAX_MB    = 30   // fichiers rejetés d'emblée (au-delà, même la compression serait trop lente)

/**
 * Fil de discussion réutilisable, style WhatsApp, intégré (non flottant).
 * Utilisé à la fois côté UserDashboard (1 seule conversation avec l'admin)
 * et côté AdminDashboard (thread d'une conversation sélectionnée).
 * Supporte l'envoi de texte, d'une ou plusieurs images, ou des deux (images + légende).
 */
export default function ChatThread({
  messages      = [],
  loading       = false,
  isMine        = () => false,   // (message) => boolean — aligne la bulle à droite si vrai
  onSend        = async () => {},   // (text, imageFiles[]) => Promise
  sending       = false,
  placeholder   = 'Votre message…',
  emptyTitle    = 'Aucun message',
  emptyHint     = 'Démarrez la conversation.',
  inputDefault  = '',
  disabled      = false,
  banner        = null,   // { img, breed, title, price } — annonce concernée par la conversation
  onDeleteMessage      = null,   // (messageId, forEveryone) => Promise — active la suppression de message si fourni
  onDeleteConversation = null,   // () => Promise — affiche le bouton "supprimer la conversation" si fourni
  peerStatus            = null,   // { online: boolean, lastActive: string|null } — statut du correspondant
}) {
  const [input, setInput]           = useState(inputDefault)
  const [imageFiles, setImageFiles] = useState([])   // [{ file, previewUrl }]
  const [imageError, setImageError] = useState('')
  const [compressing, setCompressing] = useState(false)
  const [lightbox, setLightbox]     = useState(null) // { images: string[], index: number }
  const [menuFor, setMenuFor]       = useState(null) // _id du message dont le menu d'options est ouvert
  const [confirmAction, setConfirmAction] = useState(null) // { type: 'message', id, forEveryone } | { type: 'conversation' }

  const endRef         = useRef(null)
  const inputRef        = useRef(null)
  const fileRef          = useRef(null)
  const imageFilesRef    = useRef([])

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  // Garde une référence à jour pour le nettoyage au démontage
  useEffect(() => { imageFilesRef.current = imageFiles }, [imageFiles])
  useEffect(() => {
    return () => { imageFilesRef.current.forEach(f => URL.revokeObjectURL(f.previewUrl)) }
  }, [])

  // Navigation clavier dans la lightbox (← → Échap)
  useEffect(() => {
    if (!lightbox) return
    const onKey = (e) => {
      if (e.key === 'Escape')    setLightbox(null)
      if (e.key === 'ArrowLeft')  lbStep(-1)
      if (e.key === 'ArrowRight') lbStep(1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightbox]) // eslint-disable-line

  const handlePickImages = () => {
    if (disabled || sending || compressing || imageFiles.length >= MAX_IMAGES) return
    fileRef.current?.click()
  }

  const handleFileChange = async (e) => {
    const selected = Array.from(e.target.files || [])
    e.target.value = '' // permet de resélectionner les mêmes fichiers ensuite
    if (selected.length === 0) return

    setImageError('')
    const remainingSlots = MAX_IMAGES - imageFiles.length
    let ignoredCount = 0
    const toProcess = []

    for (const file of selected) {
      if (toProcess.length >= remainingSlots) { ignoredCount++; continue }
      if (!file.type.startsWith('image/')) { ignoredCount++; continue }
      if (file.size > HARD_MAX_MB * 1024 * 1024) { ignoredCount++; continue }
      toProcess.push(file)
    }

    if (toProcess.length > 0) {
      setCompressing(true)
      try {
        // Redimensionne/recompresse chaque image pour rester sous la limite
        // Cloudinary (10 Mo) — la plupart des photos de téléphone en sortent bien en dessous.
        const compressed = await compressImages(toProcess)
        const accepted = compressed.map((file) => ({ file, previewUrl: URL.createObjectURL(file) }))
        setImageFiles(prev => [...prev, ...accepted])
      } catch {
        setImageError("Erreur lors de la préparation des images. Réessayez.")
      } finally {
        setCompressing(false)
      }
    }

    if (ignoredCount > 0) {
      setImageError(`${ignoredCount} image(s) ignorée(s) — maximum ${MAX_IMAGES} images (JPG, PNG, WebP).`)
    }
    setTimeout(() => inputRef.current?.focus(), 30)
  }

  const removeImageAt = (idx) => {
    setImageFiles(prev => {
      const target = prev[idx]
      if (target) URL.revokeObjectURL(target.previewUrl)
      return prev.filter((_, i) => i !== idx)
    })
  }

  const handleSend = async () => {
    const text = input.trim()
    if ((!text && imageFiles.length === 0) || sending || disabled) return

    const filesToSend = imageFiles.map(f => f.file)
    setInput('')
    setImageFiles(prev => { prev.forEach(f => URL.revokeObjectURL(f.previewUrl)); return [] })
    setImageError('')
    await onSend(text, filesToSend)
    inputRef.current?.focus()
  }

  const canSend = (input.trim() || imageFiles.length > 0) && !sending && !disabled

  const openLightbox = (urls, index) => setLightbox({ images: urls, index })
  const lbStep = (delta) => setLightbox(lb => lb
    ? { ...lb, index: (lb.index + delta + lb.images.length) % lb.images.length }
    : lb)

  // ── Exécute l'action de suppression confirmée (message ou conversation) ──
  const runConfirmedDelete = async () => {
    const action = confirmAction
    setConfirmAction(null)
    if (!action) return
    if (action.type === 'conversation') {
      await onDeleteConversation?.()
    } else {
      await onDeleteMessage?.(action.id, action.forEveryone)
    }
  }

  return (
    <div className="cthread">

      {/* ── Barre de statut : en ligne/hors ligne du correspondant + suppression de la conversation ── */}
      {(peerStatus || onDeleteConversation) && (
        <div className="cthread-status-bar">
          <div className="cthread-status-left">
            {peerStatus && (
              <>
                <span className={`cthread-status-dot${peerStatus.online ? ' online' : ''}`} />
                <span className="cthread-status-text">
                  {peerStatus.online
                    ? 'En ligne'
                    : peerStatus.lastActive
                      ? `Vu ${fmtLastSeen(peerStatus.lastActive)}`
                      : 'Hors ligne'}
                </span>
              </>
            )}
          </div>
          {onDeleteConversation && (
            <button
              className="cthread-status-delete"
              onClick={() => setConfirmAction({ type: 'conversation' })}
              title="Supprimer la conversation"
              type="button"
            >
              <IcoTrash />
            </button>
          )}
        </div>
      )}

      {/* ── Barre annonce (identique côté user et admin) ── */}
      {banner && (
        <div className="cthread-banner">
          {banner.img && (
            <img className="cthread-banner-img" src={banner.img} alt={banner.title} loading="lazy" />
          )}
          <div className="cthread-banner-info">
            <div className="cthread-banner-breed">{banner.breed}</div>
            <div className="cthread-banner-title">{banner.title}</div>
          </div>
          {(banner.price || banner.price === 0) && (
            <div className="cthread-banner-price">{fmtPrice(banner.price)}</div>
          )}
        </div>
      )}

      <div className="cthread-msgs">
        {loading && (
          <div className="cthread-hint">
            <div className="cthread-spinner" />
            <p>Chargement de la conversation…</p>
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="cthread-hint">
            <IcoMessages />
            <p className="cthread-hint-title">{emptyTitle}</p>
            <p className="cthread-hint-sub">{emptyHint}</p>
          </div>
        )}

        {!loading && messages.map((m, i) => {
          const mine    = isMine(m)
          const deleted = !!m.deletedForEveryone
          const imgs    = deleted ? [] : (m.images || [])
          const shown   = imgs.slice(0, 4)
          const canDelete = !deleted && typeof onDeleteMessage === 'function'
          return (
            <div key={m._id || i} className={`cthread-row ${mine ? 'out' : 'in'}`}>
              <div className="cthread-inner">
                {!mine && <div className="cthread-av">{initialsOf(m.sender?.name)}</div>}
                <div className="cthread-bubble-wrap">
                  <div className="cthread-bubble-line">
                    {canDelete && (
                      <button
                        className="cthread-msg-menu-btn"
                        onClick={() => setMenuFor(id => id === m._id ? null : m._id)}
                        title="Options"
                        type="button"
                      >
                        <IcoDots />
                      </button>
                    )}
                    <div className={`cthread-bubble ${mine ? 'bubble-out' : 'bubble-in'}${imgs.length ? ' has-image' : ''}${deleted ? ' bubble-deleted' : ''}`}>
                      {deleted ? (
                        <div className="cthread-bubble-text cthread-deleted-text">🚫 Message supprimé</div>
                      ) : (
                        <>
                          {imgs.length > 0 && (
                            <div className={`cthread-img-grid n${shown.length}`}>
                              {shown.map((img, idx) => (
                                <div
                                  key={idx}
                                  className="cthread-img-cell"
                                  onClick={() => openLightbox(imgs.map(x => x.url), idx)}
                                >
                                  <img src={img.url} alt="Pièce jointe" loading="lazy" />
                                  {idx === 3 && imgs.length > 4 && (
                                    <div className="cthread-img-more">+{imgs.length - 4}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {m.body && <div className="cthread-bubble-text">{m.body}</div>}
                        </>
                      )}
                    </div>
                    {menuFor === m._id && (
                      <>
                        <div className="cthread-menu-backdrop" onClick={() => setMenuFor(null)} />
                        <div className={`cthread-msg-menu ${mine ? 'menu-out' : 'menu-in'}`}>
                          <button
                            type="button"
                            onClick={() => { setConfirmAction({ type: 'message', id: m._id, forEveryone: false }); setMenuFor(null) }}
                          >
                            Supprimer pour moi
                          </button>
                          {mine && (
                            <button
                              type="button"
                              onClick={() => { setConfirmAction({ type: 'message', id: m._id, forEveryone: true }); setMenuFor(null) }}
                            >
                              Supprimer pour tout le monde
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="cthread-ts">
                    {!mine && m.sender?.name && <span>{m.sender.name} · </span>}
                    {new Date(m.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    {mine && !deleted && (
                      <span
                        className={`cthread-ticks${m.readByRecipient ? ' read' : ''}`}
                        title={m.readByRecipient ? 'Vu' : 'Envoyé'}
                      >
                        <IcoDoubleCheck />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
        <div ref={endRef} />
      </div>

      {/* ── Aperçu des images sélectionnées avant envoi ── */}
      {compressing && (
        <div className="cthread-preview-row">
          <div className="cthread-compress-spinner" />
          <div className="cthread-preview-info">Compression des images…</div>
        </div>
      )}
      {!compressing && imageFiles.length > 0 && (
        <div className="cthread-preview-row">
          <div className="cthread-preview-strip">
            {imageFiles.map((img, i) => (
              <div key={i} className="cthread-preview-item">
                <img src={img.previewUrl} alt="Aperçu" />
                <button className="cthread-preview-item-remove" onClick={() => removeImageAt(i)} title="Retirer">
                  <IcoX />
                </button>
              </div>
            ))}
          </div>
          <div className="cthread-preview-count">{imageFiles.length}/{MAX_IMAGES}</div>
        </div>
      )}
      {imageError && <div className="cthread-img-error">{imageError}</div>}

      <div className="cthread-input-wrap">
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/png, image/jpeg, image/webp"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <button
          className="cthread-attach"
          onClick={handlePickImages}
          disabled={disabled || sending || compressing || imageFiles.length >= MAX_IMAGES}
          title={imageFiles.length >= MAX_IMAGES ? `Maximum ${MAX_IMAGES} images` : 'Joindre des images'}
          type="button"
        >
          {compressing ? <span className="cthread-attach-spinner" /> : <IcoImage />}
        </button>
        <input
          ref={inputRef}
          className="cthread-input"
          placeholder={imageFiles.length > 0 ? 'Ajouter une légende (optionnel)…' : placeholder}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
          }}
          disabled={disabled || sending}
          maxLength={2000}
        />
        <button
          className="cthread-send"
          onClick={handleSend}
          disabled={!canSend || compressing}
          title="Envoyer"
        >
          <IcoSend />
        </button>
      </div>

      {/* ── Confirmation de suppression (message ou conversation) ── */}
      {confirmAction && (
        <div className="cthread-confirm-overlay" onClick={() => setConfirmAction(null)}>
          <div className="cthread-confirm-box" onClick={e => e.stopPropagation()}>
            <div className="cthread-confirm-icon"><IcoTrash /></div>
            <div className="cthread-confirm-title">
              {confirmAction.type === 'conversation' ? 'Supprimer la conversation ?' : 'Supprimer ce message ?'}
            </div>
            <div className="cthread-confirm-sub">
              {confirmAction.type === 'conversation'
                ? "Tous les messages de cette conversation seront supprimés de votre côté. Cette action est irréversible."
                : confirmAction.forEveryone
                  ? "Ce message sera supprimé pour tout le monde. Cette action est irréversible."
                  : "Ce message sera supprimé uniquement de votre côté."}
            </div>
            <div className="cthread-confirm-actions">
              <button className="cthread-confirm-cancel" type="button" onClick={() => setConfirmAction(null)}>
                Annuler
              </button>
              <button className="cthread-confirm-danger" type="button" onClick={runConfirmedDelete}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Zoom plein écran avec navigation ── */}
      {lightbox && (
        <div className="cthread-lightbox" onClick={() => setLightbox(null)}>
          <button className="cthread-lightbox-close" onClick={() => setLightbox(null)}>
            <IcoX />
          </button>

          {lightbox.images.length > 1 && (
            <button
              className="cthread-lightbox-nav cthread-lightbox-prev"
              onClick={e => { e.stopPropagation(); lbStep(-1) }}
            >
              <IcoChevronLeft />
            </button>
          )}

          <img
            src={lightbox.images[lightbox.index]}
            alt="Aperçu agrandi"
            onClick={e => e.stopPropagation()}
          />

          {lightbox.images.length > 1 && (
            <button
              className="cthread-lightbox-nav cthread-lightbox-next"
              onClick={e => { e.stopPropagation(); lbStep(1) }}
            >
              <IcoChevronRight />
            </button>
          )}

          {lightbox.images.length > 1 && (
            <div className="cthread-lightbox-counter">
              {lightbox.index + 1} / {lightbox.images.length}
            </div>
          )}
        </div>
      )}
    </div>
  )
}